import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notify } from '@/lib/notify';
import { apiErrorMessage } from '@/api/errors';
import { POLL_REALTIME } from '@/lib/polling';
import { migrationsApi } from '@/features/migrations/api';
import { isActive } from '@/features/migrations/status';
import type { CreateMigrationInput, Migration } from '@/features/migrations/types';

const keys = {
  all: ['mail-migrations'] as const,
  list: (mailboxId?: string) => ['mail-migrations', 'list', mailboxId ?? 'all'] as const,
  detail: (id: string) => ['mail-migrations', 'detail', id] as const,
};

/** Polls every 5 s while any job is active; idle otherwise. */
export const useMigrationsList = (mailboxId?: string) =>
  useQuery({
    queryKey: keys.list(mailboxId),
    queryFn: () => migrationsApi.list(mailboxId),
    refetchInterval: (q) => ((q.state.data as Migration[] | undefined)?.some(isActive) ? POLL_REALTIME : false),
  });

export const useMigration = (id: string | null) =>
  useQuery({
    queryKey: keys.detail(id ?? ''),
    queryFn: () => migrationsApi.get(id!),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data && isActive(q.state.data as Migration) ? POLL_REALTIME : false),
  });

export function useCreateMigration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: CreateMigrationInput) => migrationsApi.create(b),
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); notify.success('Migration queued'); },
    onError: (e) => notify.error(apiErrorMessage(e, 'Could not start the migration')),
  });
}

export function useVerifySource() {
  return useMutation({ mutationFn: migrationsApi.verifySource });
}

export function useCancelMigration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => migrationsApi.cancel(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); notify.success('Cancellation requested'); },
    onError: (e) => notify.error(apiErrorMessage(e, 'Could not cancel the migration')),
  });
}

export function useRerunMigration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; sourcePassword: string }) => migrationsApi.rerun(v.id, v.sourcePassword),
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); notify.success('Incremental migration queued'); },
    onError: (e) => notify.error(apiErrorMessage(e, 'Could not rerun the migration')),
  });
}
